import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import apiClient from '../../utils/api';
import { FaEdit, FaTrash, FaPlus, FaImage } from 'react-icons/fa';

interface PorcionPrecio {
  porciones: string;
  precio: number;
}

interface Producto {
  productoId: string;
  nombre: string;
  descripcion: string;
  precio?: number;
  precioTexto?: string;
  imageUrl?: string;
  s3Key?: string;
  category: string;
  published: string;
  displayOrder?: number;
  porcionesPrecios?: PorcionPrecio[];
}

const ProductosManagement = () => {
  const [productos, setProductos] = useState<Producto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProducto, setEditingProducto] = useState<Producto | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    precio: '',
    precioTexto: '',
    category: 'sin-categoria',
    published: 'false',
    displayOrder: '',
  });

  const [porcionesPrecios, setPorcionesPrecios] = useState<Array<{ porciones: string; precio: string }>>([]);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  useEffect(() => {
    loadProductos();
  }, []);

  const loadProductos = async () => {
    try {
      const response = (await apiClient.getProductos()) as { productos: Producto[] };
      setProductos(response.productos || []);
    } catch (error) {
      console.error('Error loading productos:', error);
      alert('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingProducto(null);
    setFormData({
      nombre: '',
      descripcion: '',
      precio: '',
      precioTexto: '',
      category: 'sin-categoria',
      published: 'false',
      displayOrder: '',
    });
    setPorcionesPrecios([]);
    setSelectedFile(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const openEditModal = (producto: Producto) => {
    setEditingProducto(producto);
    setFormData({
      nombre: producto.nombre,
      descripcion: producto.descripcion,
      precio: producto.precio?.toString() || '',
      precioTexto: producto.precioTexto || '',
      category: producto.category,
      published: producto.published,
      displayOrder: producto.displayOrder?.toString() || '',
    });
    setPorcionesPrecios(
      (producto.porcionesPrecios || []).map((pp) => ({
        porciones: pp.porciones,
        precio: pp.precio.toString(),
      }))
    );
    setSelectedFile(null);
    setImagePreview(producto.imageUrl || null);
    setShowModal(true);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const addPorcionRow = () => {
    setPorcionesPrecios([...porcionesPrecios, { porciones: '', precio: '' }]);
  };

  const removePorcionRow = (index: number) => {
    setPorcionesPrecios(porcionesPrecios.filter((_, i) => i !== index));
  };

  const updatePorcionRow = (index: number, field: 'porciones' | 'precio', value: string) => {
    const updated = [...porcionesPrecios];
    updated[index] = { ...updated[index], [field]: value };
    setPorcionesPrecios(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      let s3Key = editingProducto?.s3Key;

      if (selectedFile) {
        setUploadingImage(true);
        const uploadResponse = (await apiClient.getProductoUploadUrl(
          selectedFile.name,
          selectedFile.type
        )) as { uploadUrl: string; s3Key: string };

        await apiClient.uploadToS3(uploadResponse.uploadUrl, selectedFile);
        s3Key = uploadResponse.s3Key;
        setUploadingImage(false);
      }

      const porcionesData: PorcionPrecio[] = porcionesPrecios
        .filter((pp) => pp.porciones.trim() && pp.precio)
        .map((pp) => ({ porciones: pp.porciones.trim(), precio: parseFloat(pp.precio) }));

      const data = {
        nombre: formData.nombre,
        descripcion: formData.descripcion,
        precio: formData.precio ? parseFloat(formData.precio) : undefined,
        precioTexto: formData.precioTexto,
        category: formData.category,
        published: formData.published,
        displayOrder: formData.displayOrder ? parseInt(formData.displayOrder) : undefined,
        s3Key,
        porcionesPrecios: formData.category === 'tortas' ? porcionesData : [],
      };

      if (editingProducto) {
        await apiClient.updateProducto(editingProducto.productoId, data);
      } else {
        await apiClient.createProducto(data);
      }

      setShowModal(false);
      await loadProductos();
    } catch (error: any) {
      console.error('Error saving producto:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
      setUploadingImage(false);
    }
  };

  const handleDelete = async (productoId: string) => {
    if (!confirm('¿Estás seguro de que deseas eliminar este producto?')) {
      return;
    }

    try {
      await apiClient.deleteProducto(productoId);
      await loadProductos();
    } catch (error: any) {
      console.error('Error deleting producto:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const togglePublished = async (producto: Producto) => {
    try {
      await apiClient.updateProducto(producto.productoId, {
        published: producto.published === 'true' ? 'false' : 'true',
      });
      await loadProductos();
    } catch (error: any) {
      console.error('Error toggling published:', error);
      alert(`Error: ${error.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-milo-beige-light py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-serif text-milo-black">Gestión de Productos</h1>
          <button onClick={openCreateModal} className="btn-primary flex items-center gap-2">
            <FaPlus /> Nuevo Producto
          </button>
        </div>

        {loading && !showModal ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-milo-gold"></div>
          </div>
        ) : productos.length === 0 ? (
          <div className="card text-center py-12">
            <p className="text-milo-gray text-lg">No hay productos creados</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {productos.map((producto) => (
              <div key={producto.productoId} className="card">
                {producto.imageUrl && (
                  <img
                    src={producto.imageUrl}
                    alt={producto.nombre}
                    className="w-full h-48 object-cover rounded-lg -mx-6 -mt-6 mb-4"
                    style={{ width: 'calc(100% + 3rem)' }}
                  />
                )}
                <h3 className="text-xl font-serif text-milo-black mb-2">{producto.nombre}</h3>
                <p className="text-milo-gray text-sm mb-4 line-clamp-2">{producto.descripcion}</p>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-milo-gold font-medium">
                    {producto.precio ? `$${producto.precio.toLocaleString()}` : producto.precioTexto}
                  </span>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      producto.published === 'true'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {producto.published === 'true' ? 'Publicado' : 'Borrador'}
                  </span>
                </div>
                {producto.porcionesPrecios && producto.porcionesPrecios.length > 0 && (
                  <div className="mb-4 text-xs text-milo-gray space-y-0.5">
                    {producto.porcionesPrecios.map((pp, i) => (
                      <div key={i} className="flex justify-between">
                        <span>{pp.porciones} porciones</span>
                        <span className="font-medium">${pp.precio.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => togglePublished(producto)}
                    className="btn-secondary flex-1 text-sm py-2"
                  >
                    {producto.published === 'true' ? 'Despublicar' : 'Publicar'}
                  </button>
                  <button
                    onClick={() => openEditModal(producto)}
                    className="btn-outline px-4 py-2 text-sm"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleDelete(producto.productoId)}
                    className="border-2 border-red-500 text-red-500 px-4 py-2 rounded-lg hover:bg-red-500 hover:text-white transition-all text-sm"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        <AnimatePresence>
          {showModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                className="bg-white rounded-xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <h2 className="text-2xl font-serif text-milo-black mb-6">
                  {editingProducto ? 'Editar Producto' : 'Nuevo Producto'}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="label">Nombre *</label>
                    <input
                      type="text"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      className="input-field"
                      required
                    />
                  </div>

                  <div>
                    <label className="label">Descripción</label>
                    <textarea
                      value={formData.descripcion}
                      onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                      className="input-field min-h-[100px]"
                      rows={4}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">
                        {formData.category === 'tortas' ? 'Precio torta entera' : 'Precio (número)'}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.precio}
                        onChange={(e) => setFormData({ ...formData, precio: e.target.value })}
                        className="input-field"
                        placeholder="5000"
                      />
                    </div>
                    <div>
                      <label className="label">Precio (texto)</label>
                      <input
                        type="text"
                        value={formData.precioTexto}
                        onChange={(e) => setFormData({ ...formData, precioTexto: e.target.value })}
                        className="input-field"
                        placeholder="Consultar"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="label">Categoría</label>
                    <select
                      value={formData.category}
                      onChange={(e) => {
                        setFormData({ ...formData, category: e.target.value });
                        if (e.target.value !== 'tortas') setPorcionesPrecios([]);
                      }}
                      className="input-field"
                    >
                      <option value="sin-categoria">Sin categoría</option>
                      <option value="tortas">Tortas</option>
                      <option value="pasteles">Pasteles</option>
                      <option value="galletas">Galletas</option>
                      <option value="otros">Otros</option>
                    </select>
                  </div>

                  {/* Sección precios por porciones — solo para Tortas */}
                  {formData.category === 'tortas' && (
                    <div className="border border-milo-beige-dark rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="label mb-0">Precios por porciones</label>
                        <button
                          type="button"
                          onClick={addPorcionRow}
                          className="text-sm btn-secondary py-1 px-3 flex items-center gap-1"
                        >
                          <FaPlus className="text-xs" /> Agregar
                        </button>
                      </div>
                      {porcionesPrecios.length === 0 && (
                        <p className="text-sm text-milo-gray italic">
                          Sin rangos definidos. El precio de torta entera se mostrará solo.
                        </p>
                      )}
                      {porcionesPrecios.map((pp, index) => (
                        <div key={index} className="flex gap-2 items-center">
                          <div className="flex-1">
                            <input
                              type="text"
                              value={pp.porciones}
                              onChange={(e) => updatePorcionRow(index, 'porciones', e.target.value)}
                              className="input-field text-sm"
                              placeholder="ej: 8 a 10"
                            />
                          </div>
                          <div className="flex-1">
                            <input
                              type="number"
                              step="0.01"
                              value={pp.precio}
                              onChange={(e) => updatePorcionRow(index, 'precio', e.target.value)}
                              className="input-field text-sm"
                              placeholder="Precio"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removePorcionRow(index)}
                            className="text-red-400 hover:text-red-600 p-2 flex-shrink-0"
                          >
                            <FaTrash className="text-sm" />
                          </button>
                        </div>
                      ))}
                      {porcionesPrecios.length > 0 && (
                        <p className="text-xs text-milo-gray">
                          Especificá el rango de porciones (ej: "8 a 10", "12 a 15") y el precio para cada opción.
                        </p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="label">Estado</label>
                    <select
                      value={formData.published}
                      onChange={(e) => setFormData({ ...formData, published: e.target.value })}
                      className="input-field"
                    >
                      <option value="false">Borrador</option>
                      <option value="true">Publicado</option>
                    </select>
                  </div>

                  <div>
                    <label className="label">Imagen</label>
                    <div className="mt-2">
                      <label className="btn-secondary cursor-pointer inline-flex items-center gap-2">
                        <FaImage />
                        Seleccionar imagen
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                      </label>
                      {selectedFile && (
                        <p className="text-sm text-milo-gray mt-2">{selectedFile.name}</p>
                      )}
                    </div>
                    {imagePreview && (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="mt-4 w-full h-48 object-cover rounded-lg"
                      />
                    )}
                  </div>

                  <div className="flex gap-4 pt-4">
                    <button
                      type="submit"
                      disabled={loading || uploadingImage}
                      className="btn-primary flex-1 disabled:opacity-50"
                    >
                      {uploadingImage
                        ? 'Subiendo imagen...'
                        : loading
                        ? 'Guardando...'
                        : editingProducto
                        ? 'Actualizar'
                        : 'Crear'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="btn-secondary flex-1"
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ProductosManagement;
